/* eslint-disable prefer-const */
import {
  airportHeliportStruct,
  flightlistStruct,
  msgCatStruct,
  opsMissionTypeEnum,
  parameterStruct,
  processResultStateEnum,
  property,
  targetPlatformEnum,
} from "../dataStruct";
import {
  addParameter,
  currentSignatureId,
  execAccuParameters,
  getBearing,
  getParameterOfArray,
  getParametersArray,
  getUtcTime,
  markDownProcessing,
  padLeft,
  queueCommand,
  queueCommands,
  queueParameter,
  translateToFplCoordinates,
} from "../functions";
import { aircraftStruct, fppProvider, modelEnum } from "./providers/fppProvider";
import { pmtilesElevAt } from "../../../services/terrain/terrain-pmtiles";
import { rowIdFor } from "../../../db/rowid";
import { LegParam, WantedLeg, legWrites } from "./nav-leg-writes";

export class navParameterStruct {
  liveValues: liveValueStruct;
  plannedTimesDistances: plannedTimeDistanceStruct;
  // ... more in documentation, to come!
}

export class liveValueStruct {
  pposRouteTimeToDest: number;
  pposDirectTimeToDest: number;
  pposDirectTimeToAlternate: number;
  pposRouteDistanceToDest: number;
  pposDirectDistanceToDest: number;
  pposDirectDistanceToAlternate: number;
  pposFlightProgressRoute: number;
  pposEstLandingTime: number;
  pposEstRampArrival: number;
}

export class plannedTimeDistanceStruct {
  directDistanceDestToAlternate: number;
  routeDistanceDestToAlternate: number;
  directDistanceFromOriginToDest: number;
  routeDistanceFromOriginToDest: number;
  directTimeDestToAlternate: number;
  routeTimeDestToAlternate: number;
  directTimeFromOriginToDest: number;
  routeTimeFromOriginToDest: number;
}

export class navLegStruct {
  ident: string;
  desig: string;
  geoPos: [number, number];
  estElapsedTime: number;
  totalTime: number;
  actualTimeOverhead: number;
  totalDistance: number;
  elevation: number;
  initialMagnTrack: number;
  intitialTrueTrack: number;
  estGroundSpeed: number;
  estTrueAirSpeed: number;
  estWindSpeed: number;
  estWindDir: number;
  estWindComp: number;
  outsideAirTemp: number;
  EstFuelOnBoard: number;
  plannedTotalFuelburn: number;
  actualTotalFuelBurn: number;
}

export class navLogReport {
  public plannedTimeBlockOffFoDeparture_min: number = 3;

  public terrainDataUrl: string = "";

  public CLASSCOM: any;

  constructor(classCom: any) {
    this.CLASSCOM = classCom;
  }

  async initNav(
    flightId: string,
    acft: aircraftStruct,
    terrainDataUrl: string,
    initNavLegs?: navLegStruct[],
    flightTimes?: flightlistStruct
  ): Promise<boolean> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    this.terrainDataUrl = terrainDataUrl;

    try {
      const icaoOrigin = getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams);
      const icaoDest = getParameterOfArray(flightId, "flightlist", property.icaoDest, flightlistParams);
      const icaoAlternate = getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams);

      // The three lookups need nothing from each other: they start together.
      let [origin, dest, alternate] = await Promise.all([
        this.findAirportHeliport(icaoOrigin[0].value, undefined, acft),
        this.findAirportHeliport(icaoDest[0].value, undefined, acft),
        this.findAirportHeliport(icaoAlternate[0].value, undefined, acft),
      ]);

      if (alternate == undefined) alternate = origin;

      if (icaoOrigin[0].value == "ZZZZ") {
        const param = getParameterOfArray(flightId, "flightlist", property.originCoords, flightlistParams);
        const geoPos = JSON.parse(param[0].value);
        origin[0].geoPos = geoPos;
      }
      if (icaoDest[0].value == "ZZZZ") {
        const param = getParameterOfArray(flightId, "flightlist", property.destinationCoords, flightlistParams);
        const geoPos = JSON.parse(param[0].value);
        dest[0].geoPos = geoPos;
      }
      if (icaoAlternate[0].value == "ZZZZ") {
        const param = getParameterOfArray(flightId, "flightlist", property.alternateCoords, flightlistParams);
        const geoPos = JSON.parse(param[0].value);
        alternate[0].geoPos = geoPos;
      }

      const newTimestamp = new Date().getTime();

      let navLegWpts: navLegStruct[] = [];

      const originLeg: navLegStruct = new navLegStruct();
      originLeg.ident = origin[0].icaoCode;
      originLeg.desig = origin[0].txtName;
      originLeg.geoPos = origin[0].geoPos;

      const middleLegs: navLegStruct[] = initNavLegs != undefined ? initNavLegs.slice(1, -1) : [];

      const destLeg: navLegStruct = new navLegStruct();
      destLeg.ident = dest[0].icaoCode;
      destLeg.desig = dest[0].txtName;
      destLeg.geoPos = dest[0].geoPos;

      const alternateLeg: navLegStruct = new navLegStruct();
      alternateLeg.ident = alternate[0].icaoCode;
      alternateLeg.geoPos = alternate[0].geoPos;
      alternateLeg.desig = alternate[0].txtName;

      // The elevation lookups need nothing from each other: they start together.
      // The destination keeps its own catch: a failed lookup leaves its elevation undefined.
      const [originElev, middleElevs, destElev, alternateElev] = await Promise.all([
        getElevationOfGeoPos(originLeg.geoPos, terrainDataUrl, this.CLASSCOM),
        Promise.all(middleLegs.map((leg) => getElevationOfGeoPos(leg.geoPos, terrainDataUrl, this.CLASSCOM))),
        getElevationOfGeoPos(destLeg.geoPos, terrainDataUrl, this.CLASSCOM).catch(() => {
          console.log("warn: unable to fetch elevation!");
          return undefined;
        }),
        getElevationOfGeoPos(alternateLeg.geoPos, terrainDataUrl, this.CLASSCOM),
      ]);
      originLeg.elevation = originElev;
      middleLegs.forEach((leg, i) => (leg.elevation = middleElevs[i]));
      destLeg.elevation = destElev;
      alternateLeg.elevation = alternateElev;

      navLegWpts.push(originLeg, ...middleLegs, destLeg, alternateLeg);

      await this.writeNavLegWaypoints(flightId, navLegWpts);

      navLegWpts = await this.getLegs(acft, flightId);

      const rteDistOrigToDest: number = navLegWpts[navLegWpts.length - 2].totalDistance;
      const timeRteDistOrigToDest: number = navLegWpts[navLegWpts.length - 2].totalTime;

      const depTimeParam = getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams);
      const depTime = depTimeParam[0].value;
      const estTimeArr = new Date(parseInt(depTime));
      const d1 = new Date(parseInt(depTime));
      estTimeArr.setSeconds(d1.getSeconds() + timeRteDistOrigToDest);

      const dctDistToAlt: number = getDistance(dest[0].geoPos, alternate[0].geoPos);
      const rteDistToAlt: number = dctDistToAlt;
      const timeDistToAlt: number = getMeanTime(dctDistToAlt, acft.tacticalAirspeed);
      const dctDistOrigToDest: number = getDistance(dest[0].geoPos, origin[0].geoPos);
      const timeDctDistOrigToDest: number = getMeanTime(dctDistOrigToDest, acft.tacticalAirspeed);

      const RoutetimeDestToAlt: number = getMeanTime(dctDistToAlt, acft.tacticalAirspeed);

      const checkSum: string = "0";
      // Monotonic revision counter: the displayed revision is read from the
      // `revision` column, not from a row count.
      // Stamp max(revision)+1 once and use the same value for the header + the row.
      const { nextRevision, signatureId, flightRows } = await this.startRevisionReads(flightId);
      const processReport: string = await this.createNavLog(
        flightId,
        acft,
        flightTimes,
        nextRevision,
        navLegWpts,
        flightRows
      );

      const annunciators: string = round(rteDistOrigToDest * this.m_to_nm, 0) + " trackmiles";
      const processResultState: processResultStateEnum = processResultStateEnum.ok;

      // Append-only: the rowId holds the Timestamp, so each call adds a version.
      await this.writeNavSummary(flightId, estTimeArr.getTime().toString(), rteDistOrigToDest.toString(), {
        rowId: rowIdFor([flightId, newTimestamp]),
        Timestamp: newTimestamp,
        flightId,
        signatureId,
        checksum: checkSum,
        directDistanceDestToAlternate: dctDistToAlt,
        routeDistanceDestToAlternate: rteDistToAlt,
        directDistanceFromOriginToDest: dctDistOrigToDest,
        routeDistanceFromOriginToDest: rteDistOrigToDest,
        directTimeDestToAlternate: timeDistToAlt,
        routeTimeDestToAlternate: RoutetimeDestToAlt,
        directTimeFromOriginToDest: timeDctDistOrigToDest,
        routeTimeFromOriginToDest: timeRteDistOrigToDest,
        annunciators,
        processResultState,
        processReport: processReport.replace(/\n/g, "<br/>"),
        revision: nextRevision,
        updatedAt: Date.now(),
      });
      return true;
    } catch (err) {
      console.log("could not init nav! " + err);
    }
    return false;
  }

  async updateNav(flightId: string, acft: aircraftStruct, flightTimes: flightlistStruct): Promise<boolean> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    // parameters

    const icaoOrigin = getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams);
    const icaoDest = getParameterOfArray(flightId, "flightlist", property.icaoDest, flightlistParams);
    const icaoAlternate = getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams);

    // The leg rows need nothing from the lookups: they start together.
    const legRows = this.readLegRows(flightId);
    legRows.catch(() => undefined);
    let [origin, dest, alternate] = await Promise.all([
      this.findAirportHeliport(icaoOrigin[0].value, undefined, acft),
      this.findAirportHeliport(icaoDest[0].value, undefined, acft),
      this.findAirportHeliport(icaoAlternate[0].value, undefined, acft),
    ]);

    // hack alternate
    if (alternate == undefined) alternate = origin;

    // handle ZZZZ
    if (icaoOrigin[0].value == "ZZZZ") {
      const geoPos = JSON.parse(
        getParameterOfArray(flightId, "flightlist", property.originCoords, flightlistParams)[0].value
      );
      origin[0].geoPos = geoPos;
    }
    if (icaoDest[0].value == "ZZZZ") {
      const geoPos = JSON.parse(
        getParameterOfArray(flightId, "flightlist", property.destinationCoords, flightlistParams)[0].value
      );
      dest[0].geoPos = geoPos;
    }
    if (icaoAlternate[0].value == "ZZZZ") {
      const geoPos = JSON.parse(
        getParameterOfArray(flightId, "flightlist", property.alternateCoords, flightlistParams)[0].value
      );
      alternate[0].geoPos = geoPos;
    }

    // not the most efficient way to get the route, but the maintainable one
    const navLegWpts: navLegStruct[] = await this.getLegs(acft, flightId, legRows);

    const rteDistOrigToDest: number = navLegWpts[navLegWpts.length - 2].totalDistance; // -2 to deduct the alternate
    const timeRteDistOrigToDest: number = navLegWpts[navLegWpts.length - 2].totalTime; // -2 to deduct the alternate

    // push that data to flightlist table (flow briefing e.g. reads that value, in order to not have to calculate it through legs loops)
    const depTime = getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0].value;
    const estTimeArr = new Date(parseInt(depTime));
    const d1 = new Date(parseInt(depTime));
    estTimeArr.setSeconds(d1.getSeconds() + timeRteDistOrigToDest);

    const dctDistToAlt: number = getDistance(dest[0].geoPos, alternate[0].geoPos);
    const rteDistToAlt: number = dctDistToAlt;
    const timeDistToAlt: number = getMeanTime(dctDistToAlt, acft.tacticalAirspeed);
    const dctDistOrigToDest: number = getDistance(dest[0].geoPos, origin[0].geoPos);
    const timeDctDistOrigToDest: number = getMeanTime(dctDistOrigToDest, acft.tacticalAirspeed);

    const RoutetimeDestToAlt: number = getMeanTime(dctDistToAlt, acft.tacticalAirspeed);

    const newTimestamp = new Date().getTime();

    const processResultState: processResultStateEnum = processResultStateEnum.ok;

    // nav table
    // ------------------------
    const checkSum: string = "0";
    // Monotonic revision counter (see initNav): stamp max(revision)+1 once and
    // use the same value for the markdown header + the persisted row.
    const { nextRevision, signatureId, flightRows } = await this.startRevisionReads(flightId);
    const processReport: string = await this.createNavLog(
      flightId,
      acft,
      flightTimes,
      nextRevision,
      navLegWpts,
      flightRows
    );
    const annunciators: string = round(rteDistOrigToDest * this.m_to_nm, 0) + " trackmiles";

    // Append-only: the rowId holds the Timestamp, so each call adds a version.
    await this.writeNavSummary(flightId, estTimeArr.getTime().toString(), rteDistOrigToDest.toString(), {
      rowId: rowIdFor([flightId, newTimestamp]),
      Timestamp: newTimestamp,
      flightId,
      signatureId,
      checksum: checkSum,
      directDistanceDestToAlternate: dctDistToAlt,
      routeDistanceDestToAlternate: rteDistToAlt,
      directDistanceFromOriginToDest: dctDistOrigToDest,
      routeDistanceFromOriginToDest: rteDistOrigToDest,
      directTimeDestToAlternate: timeDistToAlt,
      routeTimeDestToAlternate: RoutetimeDestToAlt,
      directTimeFromOriginToDest: timeDctDistOrigToDest,
      routeTimeFromOriginToDest: timeRteDistOrigToDest,
      annunciators,
      processResultState,
      processReport: processReport.replace(/\n/g, "<br/>"),
      revision: nextRevision,
      updatedAt: Date.now(),
    });

    return true;
  }

  async alterRoute(
    flightId: string,
    allNavlegs: navLegStruct[],
    aircraft: aircraftStruct,
    terrainDataUrl: string
  ): Promise<void> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    // important NOTE:
    // the alternate is always omitted in display in this version. We�ll find a better solution longterm!
    // on the other hand when altering the route, we�ll have to always add the alternate, as the write command
    // deletes alle points first.

    // there is a special case if a user changes the Destination: We need to update all parameters
    // that are related to this data
    // the change of origin is not an option, which means we loop only from idx number 1 (of the null-based loopcounter)

    // check if destination changed:

    const icaoAlternate = getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams);
    const nameAlternate = getParameterOfArray(flightId, "flightlist", property.alternateName, flightlistParams);
    const alternateGeoPos = JSON.parse(
      getParameterOfArray(flightId, "flightlist", property.alternateCoords, flightlistParams)[0].value
    );

    // write the destination - as it might have changed
    const destPoint = allNavlegs[allNavlegs.length - 1];

    const alternateLeg: navLegStruct = new navLegStruct();
    alternateLeg.geoPos = alternateGeoPos;
    alternateLeg.ident = icaoAlternate[0].value;
    alternateLeg.desig = nameAlternate[0].value;
    allNavlegs.push(alternateLeg);

    // The destination lookup and the missing elevations need nothing from each other: they start together.
    const [dest_p] = await Promise.all([
      this.findAirportHeliport(destPoint.ident, undefined, aircraft),
      ...allNavlegs.map(async (each) => {
        if (each.elevation == undefined) {
          each.elevation = await getElevationOfGeoPos(each.geoPos, terrainDataUrl, this.CLASSCOM);
        }
      }),
    ]);
    const dest_name = dest_p[0].txtName + " " + dest_p[0].icaoCode;

    const inputWrites: Promise<void>[] = [
      queueParameter(flightId, "flightlist", property.destName, opsMissionTypeEnum.all, dest_name, "", "", "", ""),
      queueParameter(
        flightId,
        "flightlist",
        property.icaoDest,
        opsMissionTypeEnum.all,
        dest_p[0].icaoCode,
        "",
        "",
        "",
        ""
      ),
    ];

    // assemble the Route STRING
    let routeString: string = "";
    let onePointAtMinimum: boolean = false;

    for (let i = 1; i < allNavlegs.length - 2; i++) {
      const each = allNavlegs[i];

      // FPL String
      onePointAtMinimum = true;
      if (each.ident.includes("°") || each.ident == "ZZZZ" || each.ident == "...") {
        const coords: string = translateToFplCoordinates(JSON.stringify(each.geoPos));
        routeString += coords + " ";
      } else {
        routeString += each.ident + " ";
      }
    }

    // make a DCT if no item
    if (!onePointAtMinimum) routeString = "";
    inputWrites.push(
      queueParameter(flightId, "flightlist", property.fplRoute, opsMissionTypeEnum.all, routeString, "", "", "", "")
    );
    // they settle with the flush inside writeNavLegWaypoints; the guard handles an early rejection
    inputWrites.forEach((p) => p.catch(() => undefined));

    await this.writeNavLegWaypoints(flightId, allNavlegs);
    await Promise.all(inputWrites);
  }

  // Arrival time, route distance and the nav version row leave in one flush.
  // The parameters are queued after the reads, because a read of their scope flushes them early.
  private async writeNavSummary(
    flightId: string,
    estTimeArr: string,
    routeDist: string,
    navParams: Record<string, unknown>
  ): Promise<void> {
    const writes = [
      queueParameter(flightId, "flightlist", property.estTimeArr, opsMissionTypeEnum.all, estTimeArr, "", "", "", ""),
      queueParameter(flightId, "flightlist", property.estRouteDist, opsMissionTypeEnum.all, routeDist, "", "", "", ""),
      queueCommand({ name: "navInsert", params: navParams }),
    ].map((w) => {
      w.catch(() => undefined);
      return w;
    });
    await execAccuParameters();
    await Promise.all(writes);
  }

  // The count, the signature and the flight rows need nothing from each other: they start together.
  // The flight rows are awaited later by createNavLog, so an early rejection is handled here.
  private async startRevisionReads(
    flightId: string
  ): Promise<{ nextRevision: number; signatureId: string; flightRows: Promise<any[]> }> {
    const flightRows = this.CLASSCOM.namedQuery("flightlistByFlightId", { flightId });
    flightRows.catch(() => undefined);
    const [countRows, signatureId] = await Promise.all([
      this.CLASSCOM.namedQuery("navCountByFlightId", { flightId }),
      currentSignatureId(flightId, this.CLASSCOM),
    ]);
    return { nextRevision: Number(countRows[0].n) + 1, signatureId, flightRows };
  }

  private async writeNavLegWaypoints(flightId: string, navLegs: navLegStruct[]): Promise<void> {
    // Writes only the leg rows and parameters that differ from the stored ones
    // (legWrites). The leg id is the leg's position in the route, so a waypoint
    // inserted in the middle still rewrites the legs after it.
    const wanted: WantedLeg[] = [];
    for (let i = 0; i < navLegs.length; i++) {
      const leg = navLegs[i];
      const uid = this.CLASSCOM.package_md5(flightId.toString() + "-" + (i * 1000).toString()).substring(0, 20);
      const geoJson = JSON.stringify(leg.geoPos);
      const params: LegParam[] = [
        { type: property.ident, value: leg.ident, json: geoJson },
        { type: property.desig, value: leg.ident !== leg.desig ? leg.desig : "", json: geoJson },
        { type: property.elevation, value: leg.elevation.toString(), json: "" },
      ];
      if (i > 0) {
        const storedTrack = round(((getBearing(navLegs[i - 1].geoPos, leg.geoPos) * Math.PI) / 180) * 100, 0);
        // the leg holds the form a reader gets back (degrees), not the stored one
        leg.intitialTrueTrack = round((storedTrack * 180) / Math.PI / 100, 1);
        leg.initialMagnTrack = -1;
        // No magnetic track is stored: it was always -1, and a reader takes a missing one as -1.
        params.push({ type: property.trueTrack, value: storedTrack.toString(), json: "" });
      }
      wanted.push({ legId: uid, actOverflyTime: leg.actualTimeOverhead ?? 0, params });
    }

    // A failed read writes every leg and removes none: the route must be stored, and
    // a leg that stays is removed only by a later route edit. A throw here lost a flight create, a
    // waypoint time or a route save.
    let storedLegs: any[] = [];
    let storedParams: any[] = [];
    try {
      storedLegs = await this.CLASSCOM.namedQueryOrThrow("navsegLegsByFlightId", { flightId });
      if (storedLegs.length > 0) {
        storedParams = await this.CLASSCOM.namedQueryOrThrow("paramsByHashListSuffixMission", {
          parameterHashes: storedLegs.map((l: any) => l.legId),
          opsMissionType: opsMissionTypeEnum.all,
          tableSuffix: "navseg",
        });
      }
    } catch (err) {
      console.error("navlog: could not read the stored legs, writing every leg", err);
      storedLegs = [];
      storedParams = [];
    }

    const writes = legWrites(wanted, storedLegs, storedParams);
    let newTimestamp: number = new Date().getTime();
    const pending: Promise<void>[] = [];
    // Native writes at once, so each write is awaited: a reader takes the insert
    // order of the leg rows as the leg order. Web queues for the flush below.
    const native = !this.CLASSCOM.batchedWrites;
    // the guard stops an early rejection from being unhandled before the flush
    const send = async (p: Promise<void>): Promise<void> => {
      p.catch(() => undefined);
      pending.push(p);
      if (native) await p;
    };
    for (const row of writes.rows) {
      await send(
        queueCommand({
          name: "navsegInsert",
          params: {
            rowId: rowIdFor([flightId, row.legId]),
            Timestamp: newTimestamp,
            flightId,
            sequence: 0,
            legId: row.legId,
            actOverflyTime: row.actOverflyTime,
            updatedAt: new Date().getTime(),
          },
        })
      );
      newTimestamp += 1;
    }
    const updatedAt = new Date().getTime();
    // Web tolerates a terminal reject of an obsolete mark: a later route edit marks the leg again.
    await send(
      queueCommands(
        writes.obsolete.map((legId) => ({
          name: "navsegObsoleteByRowId",
          params: { rowId: rowIdFor([flightId, legId]), updatedAt },
        })),
        true
      )
    );
    for (const p of writes.params) {
      await addParameter(p.legId, "navseg", p.type, opsMissionTypeEnum.all, p.value, "", p.json, "", "", true);
    }

    await execAccuParameters();
    await Promise.all(pending);
  }

  async getNavHash(flightId: string, md5: any): Promise<string> {
    const result = await this.CLASSCOM.namedQuery("navAllByFlightId", { flightId });
    return md5(JSON.stringify(result));
  }

  async getProcessResultState(flightId: string): Promise<string> {
    const result = await this.CLASSCOM.namedQuery("navProcessResultStateByFlight", { flightId });

    if (result.length == 0) {
      return "NIL";
    }

    if (result[0].processResultState == 0) {
      return "N/A";
    }

    if (result[0].processResultState == 1) {
      return "OK";
    }

    if (result[0].processResultState == 2) {
      return "WARN";
    }

    if (result[0].processResultState == 3) {
      return "FAIL";
    }

    return "N/A";
  }

  async getPlannedTimesAndDistances(flightId: string): Promise<plannedTimeDistanceStruct> {
    const retV: plannedTimeDistanceStruct = new plannedTimeDistanceStruct();
    const result = await this.CLASSCOM.namedQuery("navPlannedTimesAndDistancesByFlight", { flightId });
    retV.directDistanceDestToAlternate = result[0].directDistanceDestToAlternate;
    retV.directDistanceFromOriginToDest = result[0].directDistanceFromOriginToDest;
    retV.directTimeDestToAlternate = result[0].directTimeDestToAlternate;
    retV.directTimeFromOriginToDest = result[0].directTimeFromOriginToDest;
    retV.routeDistanceDestToAlternate = result[0].routeDistanceDestToAlternate;
    retV.routeDistanceFromOriginToDest = result[0].routeDistanceFromOriginToDest;
    retV.routeTimeFromOriginToDest = result[0].routeTimeFromOriginToDest;
    retV.routeTimeDestToAlternate = result[0].routeTimeDestToAlternate;
    return retV;
  }

  async createNavLog(
    flightId: string,
    aircraft: aircraftStruct,
    currTimes: flightlistStruct,
    revisionNo?: number,
    legsGiven?: navLegStruct[],
    flightRowsStarted?: Promise<any[]>
  ): Promise<string> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    try {
      const allLegs_lst: navLegStruct[] = legsGiven ?? (await this.getLegs(aircraft, flightId));

      let dTime: number = 0;
      let aTime: number = 0;
      if (currTimes !== undefined) {
        if (currTimes.actDepTime !== undefined) {
          dTime = currTimes.actDepTime.getTime();
        }

        if (currTimes.actArrTime !== undefined) {
          aTime = currTimes.actArrTime.getTime();
        }
      }

      // set departure landingtime to first and last leg (except alternate)
      allLegs_lst[0].actualTimeOverhead = dTime;
      allLegs_lst[allLegs_lst.length - 2].actualTimeOverhead = aTime;

      // -2 due as alternate must be ignored
      // const routeDistFromOriginToDest: number = Math.round(allLegs_lst[allLegs_lst.length - 2].totalDistance);
      const routeTimeFromOriginToDest: number = allLegs_lst[allLegs_lst.length - 2].totalTime;

      const FlightNumber = (
        await (flightRowsStarted ?? this.CLASSCOM.namedQuery("flightlistByFlightId", { flightId }))
      )[0].flightNumber;
      // Revision is the value being stamped on this recalc (max(revision)+1),
      // passed by the recalc callers so the header and the persisted row stay in
      // sync. Display-only callers (e.g. combined flight-doc render) omit it and
      // get the current stored MAX(revision).
      const revision: string = String(
        revisionNo ?? (await this.CLASSCOM.namedQuery("navCountByFlightId", { flightId }))[0].n
      );
      const utcTimeNow = getUtcTime(new Date());
      const issueTime =
        padLeft(utcTimeNow.getHours().toString(), "0", 2) +
        ":" +
        padLeft(utcTimeNow.getMinutes().toString(), "0", 2) +
        " UTC";
      const estTimeDep: Date = new Date(
        Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
      );
      const estTimeDep_gui_LT =
        padLeft(estTimeDep.getHours().toString(), "0", 2) +
        ":" +
        padLeft(estTimeDep.getMinutes().toString(), "0", 2) +
        " LT";
      const estTimeDep_utc = getUtcTime(estTimeDep);
      const estTimeDep_gui_UTC =
        padLeft(estTimeDep_utc.getHours().toString(), "0", 2) +
        ":" +
        padLeft(estTimeDep_utc.getMinutes().toString(), "0", 2) +
        " UTC";
      const originName: string = getParameterOfArray(
        flightId,
        "flightlist",
        property.originName,
        flightlistParams
      )[0].value.trim();
      const destName: string = getParameterOfArray(
        flightId,
        "flightlist",
        property.destName,
        flightlistParams
      )[0].value.trim();
      const altName: string = getParameterOfArray(
        flightId,
        "flightlist",
        property.alternateName,
        flightlistParams
      )[0].value.trim();

      const ete: number = routeTimeFromOriginToDest;
      const h: number = Math.floor(ete / 3600);
      const min: number = Math.round((ete - h * 3600) / 60);
      const eteHHmm: string = padLeft(h.toString(), "0", 2) + ":" + padLeft(min.toString(), "0", 2);
      let elevSpread: string = "N/A";

      // elevation
      const origin_elev: number = allLegs_lst[0].elevation;
      const dest_elev: number = allLegs_lst[allLegs_lst.length - 2].elevation;

      const spread: number = round((dest_elev - origin_elev) * this.m_to_feet, 0);
      elevSpread = spread.toString() + " ft";

      // calc appropriate arr time
      const d1 = new Date();
      let estTimeArr = new Date(estTimeDep);
      estTimeArr.setSeconds(d1.getSeconds() + ete);

      const estTimeArr_gui_LT =
        padLeft(estTimeArr.getHours().toString(), "0", 2) +
        ":" +
        padLeft(estTimeArr.getMinutes().toString(), "0", 2) +
        " LT";
      estTimeArr = getUtcTime(estTimeArr);

      const estTimeArr_gui_UTC =
        padLeft(estTimeArr.getHours().toString(), "0", 2) +
        ":" +
        padLeft(estTimeArr.getMinutes().toString(), "0", 2) +
        " UTC";

      const edeNm: number = Math.round(allLegs_lst[allLegs_lst.length - 2].totalDistance * this.m_to_nm);

      const mdText_lst: string[] = [];

      mdText_lst.push("## NAVIGATIONAL LOG");
      //  mdText_lst.push("![](width:250px;sql:SELECT value FROM fpp_files_v1 where codeKey=111163134); \n")
      mdText_lst.push("flight **{FlightNumber}** - revision {revision} @  {issueTime} \n");
      mdText_lst.push(
        "ETD **{estTimeDep_gui_UTC} / {estTimeDep_gui_LT}**:   **{originName}** to **{destName}**,  Alternate {altName}\n"
      );
      mdText_lst.push("_____________________");
      mdText_lst.push(" - EDE **{edeNm} nm** - elevation spread **{elevSpreadFt} ft** \n ");
      mdText_lst.push(" - ETE **{eteHHmm}**  - ETA **{estTimeArr_gui_UTC} / {estTimeArr_gui_LT}** \n");
      mdText_lst.push("");
      mdText_lst.push("");
      mdText_lst.push(
        "| IDENT    | LATITUDE LONGITUDE  | EET | TTLT | ATO | DIS | ELEV | MT / TT | TAS / GS | WIND (COMP) | OAT (TDV) | EFOB | PBRN |"
      );
      mdText_lst.push(
        "|:---------|--------------------:|-----|------|-----|-----|------|-----------|----------|-------------|-----------|------|------|"
      );

      for (let i = 0; i < allLegs_lst.length; i++) {
        mdText_lst.push(this.addNavLogTableRow(allLegs_lst[i]));
        if (i == allLegs_lst.length - 2) {
          // add an ampty row (before alternate)
          mdText_lst.push(
            "|          |                     |     |      |     |     |      |           |          |             |           |      |      |"
          );
        }
      }
      let mdString: string = "";
      for (let i = 0; i < mdText_lst.length; i++) {
        mdString += mdText_lst[i] + "\n";
      }

      mdString = mdString.replace("{FlightNumber}", FlightNumber);
      mdString = mdString.replace("{revision}", revision);
      mdString = mdString.replace("{issueTime}", issueTime);
      mdString = mdString.replace("{destName}", destName);
      mdString = mdString.replace("{altName}", altName);
      mdString = mdString.replace("{originName}", originName);
      mdString = mdString.replace("{estTimeDep_gui_UTC}", estTimeDep_gui_UTC);
      mdString = mdString.replace("{estTimeDep_gui_LT}", estTimeDep_gui_LT);
      mdString = mdString.replace("{estTimeArr_gui_UTC}", estTimeArr_gui_UTC);
      mdString = mdString.replace("{estTimeArr_gui_LT}", estTimeArr_gui_LT);
      mdString = mdString.replace("{edeNm}", edeNm.toString());
      mdString = mdString.replace("{eteHHmm}", eteHHmm.toString());
      mdString = mdString.replace("{elevSpreadFt}", elevSpread);
      mdString = mdString.replace(
        "{pic}",
        getParameterOfArray(flightId, "flightlist", property.pilotInCommand, flightlistParams)[0].value
      );
      return mdString;
    } catch (err) {
      console.log("could not create NAVLOG! + err");
      return "error creating navlog";
    }
  }

  async getNavHtmlReport(flightId: string): Promise<string> {
    const result = await this.CLASSCOM.namedQuery("navProcessReportByFlight", { flightId });
    const mdp = new markDownProcessing();
    const cCOM = this.CLASSCOM;
    const html: string = await mdp.convMarkdown(
      result[0].processReport.replace(/<br\/>/g, "\n"),
      this.CLASSCOM.appearance,
      flightId,
      opsMissionTypeEnum.all,
      this.CLASSCOM,
      new fppProvider(),
      result[0].signatureId
    );
    if (this.CLASSCOM.targetPlatform == targetPlatformEnum.server) {
      const fs = this.CLASSCOM.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = cCOM.package_open;
          open("out.html");
        }
      });
    }
    return html;
  }

  // The stored legs and their parameters. They need only the flight, so a caller
  // can start them before it knows the aircraft and hand them to getLegs.
  async readLegRows(flightId: string): Promise<{ legs: any[]; paramRows: any[] }> {
    // get all the waypoints — catalog-dispatched
    const legs = await this.CLASSCOM.namedQuery("navsegLegsByFlightId", { flightId });

    // Bulk-load all navseg params for these legs in ONE query (was 5×N queries).
    const paramRows =
      legs.length > 0
        ? await this.CLASSCOM.namedQuery("paramsByHashListSuffixMission", {
            parameterHashes: legs.map((l: any) => l.legId),
            opsMissionType: opsMissionTypeEnum.all,
            tableSuffix: "navseg",
          })
        : [];
    return { legs, paramRows };
  }

  async getLegs(
    aircraft: aircraftStruct,
    flightId: string,
    rowsStarted?: Promise<{ legs: any[]; paramRows: any[] }>
  ): Promise<navLegStruct[]> {
    try {
      const { legs, paramRows } = await (rowsStarted ?? this.readLegRows(flightId));
      return legsFromRows(legs, groupNavsegParams(paramRows), aircraft);
    } catch {
      // A failed read is an empty navlog, as it always was.
      return [];
    }
  }

  public addNavLogTableRow(leg: navLegStruct): string {
    let ident: string;
    let latLon: string;
    let eetHHmm: string;
    let ttltHHmm: string;
    let ato: string;
    let dis: string;
    let elev: string;
    let tas_gs: string;
    let wind_comp: string;
    let imt_ttt: string;
    let oat_tdv: string;
    let efob: string;
    let pbrn: string;
    ident = leg.ident.toUpperCase().trim() + " " + leg.desig;

    // to avoid the gap in markdown
    if (leg.desig == "") {
      ident = leg.ident.toUpperCase();
    }

    // eslint-disable-next-line prefer-const
    latLon = "LON: " + round(leg.geoPos[0], 3) + "  LAT: " + round(leg.geoPos[1], 3);
    const ete: number = leg.estElapsedTime;
    let h: number = Math.floor(ete / 3600);
    let min: number = Math.round((ete - h * 3600) / 60);
    eetHHmm = padLeft(h.toString(), "0", 2) + ":" + padLeft(min.toString(), "0", 2);
    if (ete == undefined) {
      eetHHmm = "00:00";
    }
    h = Math.floor(leg.totalTime / 3600);
    min = Math.round((leg.totalTime - h * 3600) / 60);
    ttltHHmm = padLeft(h.toString(), "0", 2) + ":" + padLeft(min.toString(), "0", 2);
    if (leg.totalTime == undefined) {
      ttltHHmm = "00:00";
    }
    const tf = new Date(leg.actualTimeOverhead);
    ato = padLeft(tf.getHours().toString(), "0", 2) + ":" + padLeft(tf.getMinutes().toString(), "0", 2);
    if (leg.actualTimeOverhead == 0) {
      ato = "";
    }
    dis = round(leg.totalDistance * this.m_to_nm, 1) + " nm";
    if (leg.totalDistance == undefined) {
      dis = "";
    }
    // eslint-disable-next-line prefer-const
    elev = round(leg.elevation * this.m_to_feet, 0) + " ft";
    // eslint-disable-next-line prefer-const
    oat_tdv = "";

    // true / magnetic tracks
    if (leg.totalTime > 0) {
      // means first point - not relevant
      let magnTrack_string: string = "  -  ";
      let trueTrack_string: string = "   -  ";

      if (leg.intitialTrueTrack != -1) {
        trueTrack_string = padLeft(mod(leg.intitialTrueTrack, 360).toString(), "0", 3);
      }
      if (leg.initialMagnTrack != -1) {
        magnTrack_string = padLeft(mod(leg.initialMagnTrack, 360).toString(), "0", 3);
      }

      const combTrueMagTrack: string = magnTrack_string + "/" + trueTrack_string;
      imt_ttt = combTrueMagTrack;
    } else {
      imt_ttt = "";
    }

    tas_gs = round(leg.estTrueAirSpeed * this.mps_to_kts, 0) + "/" + round(leg.estGroundSpeed * this.mps_to_kts, 0);
    efob = "";
    pbrn = "";
    wind_comp = "n/a";

    let newRow: string =
      "| **{ident}** | {latLon}      | {eet}   | {ttlt}    | {ato}    | {dis}    | {elev} | {imt_ttt}  |    {tas_gs}      | {wind_comp}     |  {oat_tdv}  | {efob} | {pbrn} |";
    newRow = newRow.replace("{ident}", ident);
    newRow = newRow.replace("{latLon}", latLon);
    newRow = newRow.replace("{eet}", eetHHmm);
    newRow = newRow.replace("{ttlt}", ttltHHmm);
    newRow = newRow.replace("{ato}", ato);
    newRow = newRow.replace("{dis}", dis);
    newRow = newRow.replace("{elev}", elev);
    newRow = newRow.replace("{imt_ttt}", imt_ttt);
    newRow = newRow.replace("{tas_gs}", tas_gs);
    newRow = newRow.replace("{wind_comp}", wind_comp);
    newRow = newRow.replace("{oat_tdv}", oat_tdv);
    newRow = newRow.replace("{efob}", efob);
    newRow = newRow.replace("{pbrn}", pbrn);
    return newRow;
  }

  private overflyProximityThr: number = 3000;

  public async setAtoPoint(
    flightId: string,
    ppos: number[],
    acft: aircraftStruct,
    wptIdx?: number,
    flightTimes?: flightlistStruct
  ) {
    try {
      const allLegs_lst: navLegStruct[] = await this.getLegs(acft, flightId);
      // find nearest point
      const minDist: number = 0;
      let idx: number = -1;
      for (let i = 0; i < allLegs_lst.length; i++) {
        const dist: number = getDistance(ppos, allLegs_lst[i].geoPos);

        if ((minDist == 0 || dist < minDist) && dist < this.overflyProximityThr * 1.5) {
          idx = i;
        }
      }

      if (wptIdx !== undefined) {
        idx = wptIdx;
      }

      if (idx !== -1) {
        if (allLegs_lst[idx].actualTimeOverhead == 0) {
          allLegs_lst[idx].actualTimeOverhead = new Date().getTime();
          console.log("updated ATO at point: " + idx);
          await this.writeNavLegWaypoints(flightId, allLegs_lst);

          await this.updateNav(flightId, acft, flightTimes);
        }
      }
    } catch (err) {
      console.log("err: overfly= err: could not ato point! " + err);
    }
  }

  // this class uses for the time being the legacy efb tool with toolId="215250278"
  // * no VFR Points supportet yet and the code search is a bit inefficient
  //   as its a like query, but for version one that will be fine.
  public toolid = 215250278;
  // converstions
  public m_to_nm: number = 0.000539957;
  public m_to_feet: number = 3.28084;
  public mps_to_kts: number = 1.94384;

  async checkAirportExists(code: string, classCom: any): Promise<boolean> {
    if (code == "ZZZZ") return true;
    const result = await classCom.namedQuery("searchIndexAirportExistsByCode", {
      toolId: this.toolid,
      likeContent: "%Airport%(" + code.toUpperCase() + ")%",
    });
    if (result.length > 0) return true;
    return false;
  }

  async nearestAirportHeliport(
    ppos: number[],
    aircraft: aircraftStruct,
    maxDist?: number,
    searchRadiusDegrees = 0.5
  ): Promise<airportHeliportStruct[]> {
    if (ppos == undefined) return [new airportHeliportStruct()];

    const minX: number = ppos[0] - searchRadiusDegrees;
    const maxX: number = ppos[0] + searchRadiusDegrees;
    const minY: number = ppos[1] - searchRadiusDegrees;
    const maxY: number = ppos[1] + searchRadiusDegrees;
    const pposRect: number[][] = [[], []];
    pposRect[0][0] = minX;
    pposRect[0][1] = minY;
    pposRect[1][0] = maxX;
    pposRect[1][1] = maxY;
    const result = await this.CLASSCOM.namedQuery("nearestAirportHeliportInBounds", {
      minX,
      maxX,
      minY,
      maxY,
    });
    const retArr: airportHeliportStruct[] = [];
    const idDuplicationCheck_lst: string[] = [];
    if (result.length > 0) {
      for (let i = 0; i < result.length; i++) {
        const res = result[i].content;
        const resf = new airportHeliportStruct();
        const match = res.match(/\(.{4}?\),/);
        const codetype = res.substr(res.length - 3);
        if (match != null) {
          resf.icaoCode = match[0].replace("(", "");
          resf.icaoCode = resf.icaoCode.replace("),", "");
        } else {
          resf.icaoCode = "ZZZZ";
        }
        resf.codeType = codetype.trim();
        const txtname = res
          .replace("(" + resf.icaoCode + ")", "")
          .replace(", " + resf.codeType, "")
          .replace("Airport ", "");
        resf.txtName = txtname.trim();
        resf.id = result[i].id;
        const obj = JSON.parse(result[i].geoJson);
        resf.geoPos = obj.geometry.coordinates;

        // fixed win compatibility
        if (resf.codeType != "ELS") {
          if (aircraft?.fppCalcModel == modelEnum.fixedWing) {
            if (resf.codeType != "HP") {
              if (!idDuplicationCheck_lst.includes(resf.id)) {
                if (maxDist != null) {
                  const dist = getDistance(ppos, resf.geoPos);
                  if (dist <= maxDist) {
                    retArr.push(resf);
                    idDuplicationCheck_lst.push(resf.id);
                  }
                } else {
                  retArr.push(resf);
                  idDuplicationCheck_lst.push(resf.id);
                }
              }
            }
          } else {
            if (!idDuplicationCheck_lst.includes(resf.id)) {
              if (maxDist != null) {
                const dist = getDistance(ppos, resf.geoPos);
                if (dist <= maxDist) {
                  retArr.push(resf);
                  idDuplicationCheck_lst.push(resf.id);
                }
              } else {
                retArr.push(resf);
                idDuplicationCheck_lst.push(resf.id);
              }
            }
          }
        }
      }

      return retArr;
    }
    return result;
  }

  async findAirportHeliport(
    query: string,
    distanceToPos: number[],
    aircraft: aircraftStruct
  ): Promise<airportHeliportStruct[]> {
    if (query == "ZZZZ") {
      const resf = new airportHeliportStruct();
      resf.txtName = "";
      resf.codeType = "";
      resf.icaoCode = "ZZZZ";

      return [resf];
    }

    if (query.length < 3) return;
    // The two searches need nothing from each other: they start together.
    // The second one checks for an exact hit (icao code) - that is relevant
    let [result, result_uniqe] = await Promise.all([
      this.CLASSCOM.namedQuery("searchIndexAirportLikeContent", {
        toolId: this.toolid,
        likeContent: "%Airport%" + query.toUpperCase() + "%",
      }),
      this.CLASSCOM.namedQuery("searchIndexAirportLikeContent", {
        toolId: this.toolid,
        likeContent: "%Airport%(" + query.toUpperCase() + ")%",
      }),
    ]);
    if (result_uniqe.length == 1) {
      result = result_uniqe;
    }
    const retArr: airportHeliportStruct[] = [];
    const idDuplicationCheck_lst: string[] = [];
    if (result != undefined) {
      for (let i = 0; i < result.length; i++) {
        const res = result[i].content;
        const resf = new airportHeliportStruct();
        const match = res.match(/\(.{4}?\),/);
        const codetype = res.substr(res.length - 3);
        if (match != null) {
          resf.icaoCode = match[0].replace("(", "");
          resf.icaoCode = resf.icaoCode.replace("),", "");
        } else {
          resf.icaoCode = "ZZZZ";
        }
        resf.codeType = codetype.trim();
        const txtname = res
          .replace("(" + resf.icaoCode + ")", "")
          .replace(", " + resf.codeType, "")
          .replace("Airport ", "");
        resf.txtName = txtname.trim();
        resf.id = result[i].id;
        // fixed win compatibility
        if (resf.codeType != "ELS") {
          if (aircraft?.fppCalcModel == modelEnum.fixedWing) {
            if (resf.codeType != "HP") {
              if (!idDuplicationCheck_lst.includes(resf.id)) {
                retArr.push(resf);
                idDuplicationCheck_lst.push(resf.id);
              }
            }
          } else {
            if (!idDuplicationCheck_lst.includes(resf.id)) {
              retArr.push(resf);
              idDuplicationCheck_lst.push(resf.id);
            }
          }
        }
      }
      if (retArr.length == 0) {
        return retArr;
      }
      // de-duped id list for the geometry lookup (preserves original "hack for duplications")
      const idList: string[] = [];
      for (let i = 0; i < retArr.length; i++) {
        if (!idList.includes(retArr[i].id)) {
          idList.push(retArr[i].id);
        }
      }
      let geometryResult;
      try {
        geometryResult = await this.CLASSCOM.namedQuery("geometryByIds", { ids: idList });
      } catch (error) {
        this.CLASSCOM.promptMessage("database error! async findAirportHeliport() " + error, msgCatStruct.err);
        return;
      }
      // check lengths are equal
      if (geometryResult.length != retArr.length) {
        this.CLASSCOM.promptMessage("database mismatch navProvider! async findAirportHeliport()", msgCatStruct.err);
        return;
      }
      for (let i = 0; i < retArr.length; i++) {
        try {
          const obj = JSON.parse(geometryResult[i].geoJson);
          retArr[i].geoPos = obj.geometry.coordinates;
        } catch (error) {
          this.CLASSCOM.promptMessage(
            "database mismatch navProvider! async findAirportHeliport() could not parse json",
            msgCatStruct.err
          );
          return;
        }
      }
      return retArr;
    }
    return [];
  }

  async getFirsOfRoute(navLegs: navLegStruct[]): Promise<string[]> {
    const retList: string[] = [];

    // what a crazy workaround

    // TODO: find firs based on ofm data
    for (let i = 0; i < navLegs.length; i++) {
      const ident = navLegs[i].ident;

      if (ident.startsWith("LO")) {
        if (!retList.includes("LOVV")) {
          retList.push("LOVV");
        }
      }
      if (ident.startsWith("LJ")) {
        if (!retList.includes("LJLA")) {
          retList.push("LJLA");
        }
      }
      if (ident.startsWith("LK")) {
        if (!retList.includes("LKAA")) {
          retList.push("LKAA");
        }
      }
      if (ident.startsWith("LS")) {
        if (!retList.includes("LSAS")) {
          retList.push("LSAS");
        }
      }
      if (ident.startsWith("LH")) {
        if (!retList.includes("LHCC")) {
          retList.push("LHCC");
        }
      }
      if (ident.startsWith("ED")) {
        if (!retList.includes("EDMM")) {
          retList.push("EDMM");
        }
      }
      if (ident.startsWith("LZ")) {
        if (!retList.includes("LZBB")) {
          retList.push("LZBB");
        }
      }
      if (ident.startsWith("LI")) {
        if (!retList.includes("LIMM")) {
          retList.push("LIMM");
        }
      }
    }

    return retList;
  }
}

export function mod(n, m): number {
  const remain = n % m;
  return Math.floor(remain >= 0 ? remain : remain + m);
}

export function round(val: number, digits: number): number {
  return Math.round(val * Math.pow(10, digits)) / Math.pow(10, digits);
}

const earthRadius: number = 3440.0652187;
const nauticalMile: number = 1852;

export function getDistance(startPoint: number[], endPoint: number[]): number {
  if (endPoint == undefined) {
    return 0;
  }
  if (startPoint == undefined) {
    return 0;
  }
  if (round(startPoint[1], 6) == round(endPoint[1], 6) && round(startPoint[0], 6) == round(endPoint[0], 6)) {
    return 0;
  }
  const V1x = earthRadius * (Math.cos((startPoint[1] * Math.PI) / 180) * Math.cos((startPoint[0] * Math.PI) / 180));
  const V1y = earthRadius * (Math.cos((startPoint[1] * Math.PI) / 180) * Math.sin((startPoint[0] * Math.PI) / 180));
  const V1z = earthRadius * Math.sin((startPoint[1] * Math.PI) / 180);
  const V2x = earthRadius * (Math.cos((endPoint[1] * Math.PI) / 180) * Math.cos((endPoint[0] * Math.PI) / 180));
  const V2y = earthRadius * (Math.cos((endPoint[1] * Math.PI) / 180) * Math.sin((endPoint[0] * Math.PI) / 180));
  const V2z = earthRadius * Math.sin((endPoint[1] * Math.PI) / 180);
  let value =
    (V1x * V2x + V1y * V2y + V1z * V2z) /
    (Math.sqrt(Math.pow(V1x, 2) + Math.pow(V1y, 2) + Math.pow(V1z, 2)) *
      Math.sqrt(Math.pow(V2x, 2) + Math.pow(V2y, 2) + Math.pow(V2z, 2)));
  if (value > 1) {
    value = 1;
  } else if (value < -1) {
    value = -1;
  }
  return Math.round(Math.acos(value) * earthRadius * nauticalMile);
}

export function getMeanTime(distance: number, meanSpeed: number): number {
  if (meanSpeed < 0.1) {
    return 0; // ms
  } else {
    return Math.round(distance / meanSpeed); // ms
  }
}

// terrain features
const _missingTileCache = new Set<string>();

export async function getTerrain(
  x: number,
  y: number,
  z: number,
  terrDataUrl: string,
  classCom: any
): Promise<number[][]> {
  const key = `${z}/${x}/${y}`;
  if (_missingTileCache.has(key)) return [];

  try {
    const tile = await getTerrainData(
      terrDataUrl.replace("{x}", x.toString()).replace("{y}", y.toString()).replace("{z}", z.toString()),
      classCom
    );

    return tile;
  } catch (err) {
    _missingTileCache.add(key);
    return [];
  }
}

export function getKeyZXY(z: number, x: number, y: number): string {
  return z.toString() + "/" + x.toString() + "/" + y.toString();
}

async function getTerrainData(url: string, classCom: any): Promise<any> {
  return new Promise((resolve, reject) => {
    if (classCom.targetPlatform == targetPlatformEnum.server) {
      try {
        fetch(url)
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          });
      } catch (err) {
        console.log("warn: could not fetch terrain! returned 0.");
      }
    } else {
      try {
        // Strip data/ wrapper — update processor wraps content in data/ but
        // copyDirectoriesToData flattens it to Directory.Data root.
        url = url.replace(/^(data\/)+/, "");

        const filePath = url;

        console.log("=ov= terrain " + filePath);

        classCom.package_fs.Filesystem.readFile({
          directory: classCom.package_fs.Directory.Data,
          path: filePath,
          encoding: classCom.package_fs.Encoding.UTF8,
        })
          .then((el) => {
            resolve(JSON.parse(el.data));
          })
          .catch((err) => {
            reject(err);
          });
      } catch (err) {
        console.log("err: loading terrain #23112 " + err);
        reject(err);
      }
    }
  });
}

export async function getElevationOfGeoPos(pos: number[], terrDataurl: string, classCom: any): Promise<number> {
  if (classCom?.targetPlatform !== targetPlatformEnum.server) {
    try {
      const pm = await pmtilesElevAt(pos[0], pos[1]);
      if (pm !== null) return pm;
    } catch (err) {
      console.warn("pmtiles-elev: unexpected throw, using JSON fallback", err);
    }
  }

  if (terrDataurl?.length < 10) {
    console.log("warn: no terrain url provided, returned 0!");
    return 0;
  }

  try {
    const zoom: number = 14;
    let x: number = lon2tile(pos[0], zoom);
    let y: number = lat2tile(pos[1], zoom);

    const data = await getTerrain(Math.floor(x), Math.floor(y), zoom, terrDataurl, classCom);

    if (data.length == 0) {
      console.warn("NavLog: could not fetch elevation! ");
      return 0;
    }

    const len = data.length - 1;

    const px = [
      Math.floor((x - Math.floor(x)) * len),
      Math.floor((y - Math.floor(y)) * len),
      Math.ceil((x - Math.floor(x)) * len),
      Math.ceil((y - Math.floor(y)) * len),
    ];

    x = x - Math.floor(x);
    y = y - Math.floor(y);

    const alt = [
      data[px[0]][px[1]][0] * (1 - y) * (1 - x),
      data[px[2]][px[1]][0] * (1 - y) * x,
      data[px[2]][px[3]][0] * y * x,
      data[px[0]][px[3]][0] * y * (1 - x),
    ];

    return round(
      alt.reduce((a, b) => a + b, 0),
      0
    );
  } catch (err) {
    console.log("could not fetch elevation! " + err);
    return 0;
  }
}

// here are the necessary pos-to-tile function to keep this standalone
function lon2tile(lon, zoom) {
  return ((lon + 180) / 360) * Math.pow(2, zoom);
}

function lat2tile(lat, zoom) {
  return (
    ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
    Math.pow(2, zoom)
  );
}

/** Navseg param rows keyed by leg id, then by parameter type. A later row for the same type wins. */
export function groupNavsegParams(
  rows: { parameterHash: string; paramTypeId: property }[]
): Map<string, Map<property, parameterStruct>> {
  const byLeg = new Map<string, Map<property, parameterStruct>>();
  for (const row of rows as any[]) {
    const p = new parameterStruct();
    p.tableSuffix = "navseg";
    p.parameter = row.paramTypeId;
    p.value = row.paramValString;
    p.blob = row.paramValBlob;
    p.json = row.paramValJson;
    p.geoJson = row.paramValGeoJson;
    p.markdown = row.paramValMarkDown;
    let inner = byLeg.get(row.parameterHash);
    if (!inner) {
      inner = new Map();
      byLeg.set(row.parameterHash, inner);
    }
    inner.set(row.paramTypeId, p);
  }
  return byLeg;
}

/**
 * Build the navlog legs of one flight from its navseg rows (in leg order) and
 * their params. Pure: `getLegs` and the briefing card loader share it.
 *
 * A leg that cannot be built (malformed point JSON) ends the list there: the
 * legs before it are returned, as `getLegs` always did.
 */
export function legsFromRows(
  legRows: { legId: string; actOverflyTime?: number }[],
  paramsByLeg: Map<string, Map<property, parameterStruct>>,
  aircraft: { tacticalAirspeed: number } | undefined
): navLegStruct[] {
  const allLegs_lst: navLegStruct[] = [];
  let accuDist: number = 0;
  let accuTotalTime: number = 0;
  let leg_latch: navLegStruct;
  try {
    for (let i = 0; i < legRows.length; i++) {
      const currLeg = legRows[i];
      const params = paramsByLeg.get(currLeg.legId);
      const point = params?.get(property.ident);
      const desig = params?.get(property.desig);
      const elevation: string = params?.get(property.elevation)?.value ?? "";

      const leg: navLegStruct = new navLegStruct();
      leg.estElapsedTime = 0;
      leg.totalDistance = 0;
      leg.totalTime = 0;
      leg.actualTimeOverhead = currLeg.actOverflyTime;

      // parseInt never throws; an unreadable elevation is NaN, as it always was.
      leg.elevation = parseInt(elevation);

      leg.ident = point?.value;
      leg.desig = desig?.value;
      leg.geoPos = point?.json !== undefined ? JSON.parse(point.json) : undefined;
      leg.intitialTrueTrack = -1;
      leg.initialMagnTrack = -1;

      if (i > 0) {
        const trueTrack: number = parseInt(params?.get(property.trueTrack)?.value ?? "");
        if (!isNaN(trueTrack) && trueTrack != -1) leg.intitialTrueTrack = round((trueTrack * 180) / Math.PI / 100, 1);

        const magTrack: number = parseInt(params?.get(property.magTrack)?.value ?? "");
        if (!isNaN(magTrack) && magTrack != -1) leg.initialMagnTrack = round((magTrack * 180) / Math.PI / 100, 1);
      }

      if (aircraft !== undefined) leg.estTrueAirSpeed = aircraft.tacticalAirspeed;
      if (aircraft !== undefined) leg.estGroundSpeed = aircraft.tacticalAirspeed;
      if (leg_latch != undefined && leg.geoPos !== undefined && leg_latch.geoPos !== undefined) {
        const distBetweenLastAndThisLeg: number = getDistance(leg_latch.geoPos, leg.geoPos);
        accuDist += distBetweenLastAndThisLeg;
        if (aircraft != undefined)
          leg.estElapsedTime = Math.round(distBetweenLastAndThisLeg / aircraft.tacticalAirspeed);
        accuTotalTime += leg.estElapsedTime;
        leg.totalDistance = accuDist;
        leg.totalTime = accuTotalTime;
      }
      allLegs_lst.push(leg);
      leg_latch = leg;
    }
  } catch (err) {
    console.log("warn: navlog legs cut short: " + err);
  }
  return allLegs_lst;
}
